import { HttpError } from '@euler/lib/request/HttpError';
import { BiDatasetDetail, BiDatasetPreview } from '@euler/model/bi/library';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';

export type BiDatasetDetailState =
  | { kind: 'loading' }
  | { kind: 'ready'; detail: BiDatasetDetail }
  /** Gone, or not one the caller can use: nothing of it is shown. */
  | { kind: 'missing' }
  | { kind: 'failed' };

export type BiDatasetPreviewState =
  | { kind: 'loading' }
  | { kind: 'ready'; preview: BiDatasetPreview }
  | { kind: 'failed' };

/** A dataset of the library with, for an uploaded one, its first rows. */
export function useBiDatasetDetail(datasetRef: string) {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiDatasetDetailState>({
    kind: 'loading',
  });
  const [preview, setPreview] = useState<BiDatasetPreviewState>({
    kind: 'loading',
  });
  const [reload, setReload] = useState(0);
  const [previewReload, setPreviewReload] = useState(0);
  useEffect(() => {
    let active = true;
    biService
      .getDataset(datasetRef)
      .then(detail => {
        if (active) setState({ kind: 'ready', detail });
      })
      .catch(error => {
        console.error('BI dataset failed', datasetRef, error);
        if (!active) return;
        setState(current =>
          error instanceof HttpError && error.code === 'resource_not_found'
            ? { kind: 'missing' }
            : current.kind === 'ready'
            ? current
            : { kind: 'failed' },
        );
      });
    return () => {
      active = false;
    };
  }, [biService, datasetRef, reload]);
  const uploaded =
    state.kind === 'ready' && state.detail.kind === BiDatasetRefKind.Upload;
  useEffect(() => {
    if (!uploaded) return;
    let active = true;
    biService
      .previewDataset(datasetRef)
      .then(rows => {
        if (active) setPreview({ kind: 'ready', preview: rows });
      })
      .catch(error => {
        console.error('BI dataset preview failed', datasetRef, error);
        if (active) setPreview({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [biService, datasetRef, uploaded, previewReload]);
  const refresh = useCallback(() => {
    setState(current =>
      current.kind === 'failed' ? { kind: 'loading' } : current,
    );
    setReload(value => value + 1);
  }, []);
  const retryPreview = useCallback(() => {
    setPreview({ kind: 'loading' });
    setPreviewReload(value => value + 1);
  }, []);
  return { state, preview, refresh, retryPreview };
}
